import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIndicadorsFiltrats } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Kpi } from '@/pages/operacio/comu';
import { csv, descarregaCsv } from '@/pages/planificacio/calendari';
import { Barres, Donut, SelectorAmbit, SelectorPeriode, type SegmentDonut } from './comu';
import { agrupaSerie, colorTipusActivacio, tendencia, usePeriode, useTemps } from './suport';

type To = 'up' | 'down' | undefined;

/** Classe de color d'una variació: `millor` indica si pujar és bo. */
function to(diferencia: number | null, millorSiPuja: boolean): To {
  if (diferencia == null || diferencia === 0) return undefined;
  return diferencia > 0 === millorSiPuja ? 'up' : 'down';
}

export function W40Indicadors() {
  const { t } = useTranslation('informes');
  const tc = useTranslation('comu').t;
  const f = useFormat();
  const temps = useTemps();
  const { periode, setPeriode, desde, fins, anteriorDesde } = usePeriode('MES');
  const [ambit, setAmbit] = useState<{ departamentId: number | ''; areaId: number | '' }>({
    departamentId: '',
    areaId: '',
  });
  const ind = useIndicadorsFiltrats({ desde, fins, ...ambit });
  const ant = useIndicadorsFiltrats({ desde: anteriorDesde, fins: desde, ...ambit });
  const d = ind.data;
  const a = ant.data;
  const pct = (v?: number | null) => (v == null ? '—' : f.percentatge(v));
  const signe = (n: number) => (n > 0 ? `+${f.numero(n)}` : f.numero(n));
  const vsAnterior = (txt: string) => t('w40.vsAnterior', { valor: txt });

  const difTemps = (actual?: number | null, anterior?: number | null) =>
    actual != null && anterior != null ? actual - anterior : null;
  const detallTemps = (dif: number | null) =>
    dif == null || dif === 0
      ? t('w40.mitjana')
      : vsAnterior(`${dif > 0 ? '+' : dif < 0 ? '−' : ''}${temps(Math.abs(dif))}`);
  const difPunts = (actual?: number | null, anterior?: number | null) =>
    actual != null && anterior != null ? Math.round(actual - anterior) : null;
  const detallPunts = (dif: number | null) =>
    dif == null || dif === 0 ? undefined : vsAnterior(t('w40.punts', { valor: signe(dif) }));

  const grups = agrupaSerie(d?.serieTemporal ?? [], periode);
  const barres = grups.map((g, i) => {
    const total = g.reduce((s, p) => s + p.total, 0);
    const etiqueta =
      periode === 'SETMANA'
        ? f.data(g[0]?.data ?? '').slice(0, 5)
        : t('w40.setmanaN', { n: i + 1 });
    return {
      clau: g[0]?.data ?? String(i),
      etiqueta,
      valor: total,
      titol: t('w40.barraTitol', {
        periode: etiqueta,
        n: total,
      }),
    };
  });
  const maxBarra = Math.max(0, ...barres.map((b) => b.valor));
  const barresColor = barres.map((b) => ({ ...b, alt: b.valor === maxBarra && maxBarra > 0 }));

  const segments: SegmentDonut[] = (d?.perTipus ?? []).map((p) => ({
    clau: p.clau,
    nom: tc(`tipusActivacio.${p.clau}`, { defaultValue: p.nom || p.clau }),
    total: p.total,
    color: colorTipusActivacio(p.clau),
  }));
  const totalTipus = segments.reduce((s, x) => s + x.total, 0);
  const totalServeis = (d?.perServei ?? []).reduce((s, x) => s + x.total, 0);

  const exporta = () => {
    if (!d) return;
    descarregaCsv('indicadors.csv', [
      csv([t('w40.indicador'), t('w40.valor')]),
      csv([t('w40.kpi.activacions'), String(d.activacions)]),
      csv([t('w40.kpi.primeraConfirmacio'), temps(d.tempsMitjaPrimeraConfirmacioSeg)]),
      csv([t('w40.kpi.escalat'), temps(d.tempsMitjaEscalatSeg)]),
      csv([t('w40.kpi.resolucio'), temps(d.tempsMitjaResolucioSeg)]),
      csv([t('w40.kpi.confirmacio'), pct(d.percentatgeConfirmacio)]),
      csv([t('w40.kpi.noLliurats'), String(d.noLliurats)]),
      csv([t('w40.kpi.ambEscalat'), String(d.ambEscalat)]),
      csv([t('w40.kpi.compliment'), pct(d.complimentTemps)]),
      '',
      csv([t('comu.servei'), t('w40.kpi.activacions')]),
      ...d.perServei.map((s) => csv([s.nom, String(s.total)])),
    ]);
  };

  const tendActivacions = d && a ? tendencia(d.activacions, a.activacions) : null;
  const difNoLliurats = d && a ? d.noLliurats - a.noLliurats : null;
  const difConfirmacio = difPunts(d?.percentatgeConfirmacio, a?.percentatgeConfirmacio);
  const difCompliment = difPunts(d?.complimentTemps, a?.complimentTemps);
  const pctEscalat = d && d.activacions > 0 ? (d.ambEscalat / d.activacions) * 100 : null;
  const pctEscalatAnt = a && a.activacions > 0 ? (a.ambEscalat / a.activacions) * 100 : null;
  const difEscalat = difPunts(pctEscalat, pctEscalatAnt);
  const difPrimera = difTemps(
    d?.tempsMitjaPrimeraConfirmacioSeg,
    a?.tempsMitjaPrimeraConfirmacioSeg,
  );
  const difEscalatTemps = difTemps(d?.tempsMitjaEscalatSeg, a?.tempsMitjaEscalatSeg);
  const difResolucio = difTemps(d?.tempsMitjaResolucioSeg, a?.tempsMitjaResolucioSeg);

  return (
    <section className="pantalla" data-screen="W-40">
      <Toolbar titol={t('titols.indicadors')}>
        <span className="t-footnote dim">
          {t('comu.rang', { desde: f.data(desde), fins: f.data(fins) })}
        </span>
      </Toolbar>
      <div className="content">
        <div className="filtres-informe">
          <SelectorPeriode valor={periode} onCanvia={setPeriode} />
          <SelectorAmbit {...ambit} onCanvia={setAmbit} />
          <div className="spacer" />
          <button type="button" className="btn btn--s" onClick={exporta} disabled={!d}>
            <Icona nom="doc-export" />
            {t('comu.exporta')}
          </button>
        </div>
        {ind.isError ? (
          <ErrorCarrega onReintenta={() => void ind.refetch()} />
        ) : !d ? (
          <Buit icona="chart-bar">{t('comu.carregant')}</Buit>
        ) : (
          <>
            <div className="grid grid-4 marge-16" data-testid="kpis">
              <Kpi
                icona="exclamationmark-triangle"
                etiqueta={t('w40.kpi.activacions')}
                valor={f.numero(d.activacions)}
                detall={
                  tendActivacions == null
                    ? undefined
                    : vsAnterior(
                        t('comu.tendencia', {
                          signe: tendActivacions > 0 ? '+' : '',
                          n: tendActivacions,
                        }),
                      )
                }
                to={to(tendActivacions, false)}
              />
              <Kpi
                icona="clock"
                etiqueta={t('w40.kpi.primeraConfirmacio')}
                valor={
                  d.tempsMitjaPrimeraConfirmacioSeg != null
                    ? f.durada(d.tempsMitjaPrimeraConfirmacioSeg)
                    : '—'
                }
                detall={detallTemps(difPrimera)}
                to={to(difPrimera, false)}
              />
              <Kpi
                icona="arrow-up-right"
                etiqueta={t('w40.kpi.escalat')}
                valor={d.tempsMitjaEscalatSeg != null ? f.durada(d.tempsMitjaEscalatSeg) : '—'}
                detall={detallTemps(difEscalatTemps)}
                to={to(difEscalatTemps, false)}
              />
              <Kpi
                icona="checkmark-circle"
                etiqueta={t('w40.kpi.resolucio')}
                valor={d.tempsMitjaResolucioSeg != null ? f.durada(d.tempsMitjaResolucioSeg) : '—'}
                detall={detallTemps(difResolucio)}
                to={to(difResolucio, false)}
              />
              <Kpi
                icona="person-check"
                etiqueta={t('w40.kpi.confirmacio')}
                valor={pct(d.percentatgeConfirmacio)}
                detall={detallPunts(difConfirmacio)}
                to={to(difConfirmacio, true)}
              />
              <Kpi
                icona="bell-slash"
                etiqueta={t('w40.kpi.noLliurats')}
                valor={f.numero(d.noLliurats)}
                detall={difNoLliurats == null ? undefined : vsAnterior(signe(difNoLliurats))}
                to={to(difNoLliurats, false)}
              />
              <Kpi
                icona="arrow-swap"
                etiqueta={t('w40.kpi.ambEscalat')}
                valor={
                  pctEscalat == null
                    ? f.numero(d.ambEscalat)
                    : t('w40.ambPercentatge', {
                        n: f.numero(d.ambEscalat),
                        pct: f.percentatge(pctEscalat),
                      })
                }
                detall={detallPunts(difEscalat)}
                to={to(difEscalat, false)}
              />
              <Kpi
                icona="flag"
                etiqueta={t('w40.kpi.compliment')}
                valor={pct(d.complimentTemps)}
                detall={detallPunts(difCompliment)}
                to={to(difCompliment, true)}
              />
            </div>
            <div className="grid grid-sidebar">
              <div className="stack">
                <div className="panel">
                  <div className="panel__head">
                    <h3>{periode === 'SETMANA' ? t('w40.perDia') : t('w40.perSetmana')}</h3>
                    <div className="spacer" />
                    <span className="t-caption-2 dim">
                      {t('comu.rang', { desde: f.data(desde), fins: f.data(fins) })}
                    </span>
                  </div>
                  <div className="panel__body">
                    {barres.length === 0 ? (
                      <Buit icona="chart-bar">{t('comu.senseDades')}</Buit>
                    ) : (
                      <Barres dades={barresColor} etiqueta={t('w40.perPeriode')} />
                    )}
                  </div>
                </div>
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w40.perServei')}</h3>
                  </div>
                  {d.perServei.length === 0 ? (
                    <Buit icona="building">{t('comu.senseDades')}</Buit>
                  ) : (
                    <table className="taula">
                      <thead>
                        <tr>
                          <th>{t('comu.servei')}</th>
                          <th className="num">{t('w40.kpi.activacions')}</th>
                          <th className="num">{t('w40.percentTotal')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.perServei.map((s) => (
                          <tr key={s.clau}>
                            <td>{s.nom}</td>
                            <td className="num">{f.numero(s.total)}</td>
                            <td className="num">
                              {totalServeis > 0
                                ? f.percentatge((s.total / totalServeis) * 100)
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              <div className="stack">
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w40.perTipus')}</h3>
                  </div>
                  <div className="panel__body distribucio">
                    {segments.length === 0 ? (
                      <Buit icona="chart-pie">{t('comu.senseDades')}</Buit>
                    ) : (
                      <>
                        <Donut segments={segments} centre={f.numero(totalTipus)} />
                        <div className="legend">
                          {segments.map((x) => (
                            <span key={x.clau}>
                              <i className="dot" style={{ background: x.color }} />
                              {t('w40.llegenda', {
                                nom: x.nom,
                                pct: f.percentatge(
                                  totalTipus > 0 ? (x.total / totalTipus) * 100 : 0,
                                ),
                              })}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w40.recurrentsTop')}</h3>
                    <div className="spacer" />
                    <Link className="btn btn--ghost btn--xs" to="/analisi/recurrents">
                      {t('comu.veureTot')}
                    </Link>
                  </div>
                  <div className="panel__body">
                    {(d.recurrents ?? []).length === 0 ? (
                      <Buit icona="arrow-clockwise">{t('w47.buit')}</Buit>
                    ) : (
                      (d.recurrents ?? []).slice(0, 3).map((r) => (
                        <div className="kv" key={`${r.serveiId}-${r.zonaId}`}>
                          <span className="kv__k">
                            {r.servei ?? '—'}
                            {r.zona ? ` · ${r.zona}` : ''}
                          </span>
                          <span className="kv__v">{t('w47.activacions', { count: r.total })}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w40.perZona')}</h3>
                  </div>
                  <div className="panel__body">
                    {d.perZona.length === 0 ? (
                      <Buit icona="map">{t('comu.senseDades')}</Buit>
                    ) : (
                      d.perZona.map((z) => (
                        <div className="kv" key={z.clau}>
                          <span className="kv__k">{z.nom}</span>
                          <span className="kv__v">{f.numero(z.total)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
